import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import { toast } from 'sonner'
import QRCodeModal from '../components/QRCodeModal'
import { Label } from "../components/ui/label"
import { Pencil } from 'lucide-react'

export default function UserManagement() {
  const [users, setUsers] = useState([])
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'sales' })
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchUsers()
  }, [])

  async function fetchUsers() {
    try {
      const data = await api.getUsers()
      setUsers(data)
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error)
      toast.error('Benutzer konnten nicht geladen werden')
    }
  }

  async function createUser(e) {
    e.preventDefault()
    setLoading(true)

    try {
      await api.createUser(newUser)
      setNewUser({ email: '', name: '', role: 'sales' })
      toast.success('Benutzer erfolgreich erstellt')
      fetchUsers()
    } catch (error) {
      console.error('Fehler beim Erstellen des Benutzers:', error)
      toast.error('Benutzer konnte nicht erstellt werden')
    } finally {
      setLoading(false)
    }
  }

  async function deleteUser(userId, userName) {
    const message = `Möchten Sie den Benutzer "${userName}" wirklich deaktivieren?\n\n` +
                   'Der Benutzer wird deaktiviert und kann sich nicht mehr anmelden.';

    if (!window.confirm(message)) return;

    try {
      await api.deleteUser(userId);
      toast.success('Benutzer wurde erfolgreich deaktiviert');
      fetchUsers();
    } catch (error) {
      console.error('Fehler beim Deaktivieren des Benutzers:', error);
      toast.error(error.message);
    }
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Benutzerverwaltung</h1>
        <Button onClick={() => navigate('/admin')}>
          Zurück zum Dashboard
        </Button>
      </div>

      <form onSubmit={createUser} className="mb-8 flex gap-4 items-end">
        <div className="flex-1">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              placeholder="E-Mail Adresse"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              required
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              placeholder="Vollständiger Name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              required
            />
          </div>
        </div>
        <Select
          value={newUser.role}
          onValueChange={(value) => setNewUser({ ...newUser, role: value })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Rolle auswählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Administrator</SelectItem>
            <SelectItem value="sales">Vertriebler</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" disabled={loading}>
          {loading ? 'Lädt...' : 'Benutzer erstellen'}
        </Button>
      </form>

      <div className="grid gap-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>E-Mail</TableHead>
              <TableHead>Rolle</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Erstellt am</TableHead>
              <TableHead>Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.role === 'admin' ? 'Administrator' : 'Vertriebler'}
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' :
                    user.status === 'invited' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {user.status === 'active' ? 'Aktiv' :
                     user.status === 'invited' ? 'Eingeladen' :
                     'Inaktiv'}
                  </span>
                </TableCell>
                <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Bearbeiten
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedUser(user)}
                    >
                      QR-Code
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteUser(user.id, user.name)}
                    >
                      Löschen
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedUser && (
        <QRCodeModal
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          userId={selectedUser.id}
          userEmail={selectedUser.email}
        />
      )}
    </div>
  )
} 